import { useTranslation } from "react-i18next";
import clsx from "clsx";
import formatPrice from "@/core/helpers/formatPrice";
import useClickAway from "@/core/helpers/useClickAway";
import { Button, Icon } from "@/shared/ui";

type Props = {
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  isConfirming: boolean;
  businessId: number;
  address: string;
  price: number;
};

export default function BusinessSellModal({
  onClose,
  onConfirm,
  businessId,
  address,
  isLoading,
  isConfirming,
  price,
}: Props) {
  const { t } = useTranslation("business");

  const ref = useClickAway(onClose);

  return (
    <div
      className={clsx(
        "absolute z-10 top-0 left-0 right-0 bottom-0 bg-[#0F0F0F]/95",
        "flex items-center justify-center",
        "animate-[--animation-fade-in]",
      )}
    >
      {isLoading && (
        <div className="flex items-center justify-center p-4">
          <div className="mz-loader" />
        </div>
      )}

      {!isLoading && (
        <div
          ref={ref}
          className={clsx(
            "w-[25.25rem] p-5 bg-gradient-to-b from-[#0F0F0F] to-[#0F0F0F]/90 rounded-[0.875rem]",
            "relative p-7.5 flex flex-col items-center lightBlueShadow",
          )}
        >
          <Button
            variant="surface"
            color="gray"
            className="absolute top-2.5 right-2.5 size-7.5 p-0"
            onClick={onClose}
          >
            <Icon name="close" className="w-3" />
          </Button>
          <div className="text-18 font-bold text-white">
            {t("business.business")} №{businessId}
          </div>
          <div className="text-15 font-medium text-white/70 mt-1">{address}</div>
          <div className="h-[1px] w-[20rem] bg-[#1F1F1F]/80 flex justify-center my-5">
            <div className="h-full w-[5rem] bg-accent-solid" />
          </div>
          <div className="mb-3.5 text-16 font-medium text-white text-center">
            {t("business.confirmSellQuestion")}
          </div>
          <div
            className={clsx(
              "flex items-center gap-2 justify-center w-full h-11 mb-3.5",
              "rounded-[0.5rem] border border-dashed border-accent-solid",
              "bg-gradient-to-b from-[#007FDB]/10 to-[#0164AC]/10",
            )}
          >
            <Icon name="rub" className="w-6 bg-accent-solid" />
            <div className="text-16 text-white font-bold">{formatPrice(price)} ₽</div>
          </div>
          <p className="mb-5 text-15 font-medium text-white/50">{t("business.moneyNotRefunded")}</p>
          <div className="grid grid-cols-2 gap-2 w-full">
            <Button
              className="gap-2.5 h-[2.625rem]"
              onClick={onConfirm}
              loading={isConfirming}
              disabled={isConfirming}
            >
              <Icon category="business" name="cash" className="w-5 h-4" />
              {t("business.yes")}
            </Button>
            <Button
              variant="solid-surface"
              className="h-[2.625rem]"
              onClick={onClose}
              loading={isConfirming}
              disabled={isConfirming}
            >
              {t("business.no")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
